import {
  column,
  dateColumn,
  defaultPage,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  multiFilter,
  numericColumn,
  PageHeaderLayout,
  RowStyles,
  simpleDataRows,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { chipCell, dateCell, priceCell, tagCell } from "src/components";
import { CommitmentStatus, Maybe } from "src/generated/graphqlTypes";
import {
  DevelopmentCommitmentsPageDevCommFragment,
  DevelopmentCommitmentsPageQuery,
  useDevelopmentCommitmentsPageMetaDataQuery,
  useDevelopmentCommitmentsPageQuery,
} from "src/routes/development-commitments/DevelopmentCommitmentsPage.generated";
import { createDevelopmentCommitmentUrl } from "src/RouteUrls";
import { commitmentStatusToTagTypeMapper } from "src/utils/mappers";
import { CreateCommitmentModal } from "./components/CreateCommitmentModal";

export function DevelopmentCommitmentsPage() {
  const metaQuery = useDevelopmentCommitmentsPageMetaDataQuery();
  const { openModal } = useModal();
  const filterDefs: FilterDefs<DevCommitmentFilter> = useMemo(() => {
    return {
      market: multiFilter({
        options: metaQuery.data?.markets || [],
        getOptionValue: ({ id }) => id,
        getOptionLabel: ({ name }) => name,
      }),
      status: multiFilter({
        options: metaQuery.data?.commitmentStatuses || [],
        getOptionValue: ({ code }) => code,
        getOptionLabel: ({ name }) => name,
      }),
    };
  }, [metaQuery]);
  const layoutState = useGridTableLayoutState<DevCommitmentFilter>({
    persistedFilter: { filterDefs, storageKey: "developmentCommitmentsFilter" },
    search: "server",
  });
  const query = useDevelopmentCommitmentsPageQuery({
    variables: { filter: { ...layoutState.filter, search: layoutState.searchString || undefined }, page: defaultPage },
  });
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.developmentCommitmentsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: {
          page: { offset: query.data?.developmentCommitmentsPage.entities?.length, limit: defaultPage.limit },
        },
      });
    }
  }, [query]);
  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(), []);
  const createRows = useCallback(
    (data: DevelopmentCommitmentsPageQuery | undefined) => simpleDataRows(data?.developmentCommitmentsPage.entities),
    [],
  );

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Development Commitments",
        actions: [{ label: "Create", onClick: () => openModal({ content: <CreateCommitmentModal /> }) }],
      }}
    >
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          as: "virtual",
          emptyFallback: "No development commitments found that matched given filters.",
          sorting: { on: "client", initial: ["lastEdited", "DESC"] },
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = { kind: "header" } | { kind: "data"; id: string; data: DevelopmentCommitmentsPageDevCommFragment };

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Commitment Name", mw: "180px", data: (data) => data.name }),
    column<Row>({ header: "Market", mw: "140px", data: (data) => data.market.name }),
    column<Row>({ header: "Trade Partner", mw: "180px", data: (data) => data.tradePartner?.name }),
    column<Row>({ header: "Cost Codes", mw: "160px", data: (data) => chipCell(data.costCodes.map((cc) => cc.number)) }),
    numericColumn<Row>({
      header: "Total Committed",
      mw: "140px",
      data: (data) => priceCell({ valueInCents: data.committedInCents, dropZero: true }),
    }),
    dateColumn<Row>({ id: "lastEdited", header: "Last Edited", mw: "140px", data: (data) => dateCell(data.updatedAt) }),
    dateColumn<Row>({ header: "Signed On", mw: "140px", data: (data) => dateCell(data.executionDate) }),
    column<Row>({
      header: "Status",
      mw: "120px",
      data: (data) => tagCell(commitmentStatusToTagTypeMapper[data.statusDetail.code], data.statusDetail.name),
    }),
  ];
}

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ id }) => createDevelopmentCommitmentUrl(id) } };
}

type DevCommitmentFilter = { market?: Maybe<Array<string>>; status?: Maybe<Array<CommitmentStatus>> };
